import type { GameMode, Grid, LineWin, MathConfig, Position, ScatterWin, SymbolDef, SymbolId } from '../types';

export interface EvalResult {
  lineWins: LineWin[];
  scatterWin: ScatterWin | null;
  freeSpinsAwarded: number;
  totalWin: number;
  capped: boolean;
}

/** Precomputed lookups so evaluate() allocates little in hot loops (simulations). */
interface Tables {
  defs: Map<SymbolId, SymbolDef>;
  paying: SymbolDef[];
  minCount: Map<SymbolId, number>;
}

const cache = new WeakMap<MathConfig, Tables>();

function tables(config: MathConfig): Tables {
  let t = cache.get(config);
  if (!t) {
    const defs = new Map(config.symbols.map((s) => [s.id, s]));
    const paying = config.symbols.filter((s) => s.pays && Object.keys(s.pays).length > 0 && !s.scatter);
    const minCount = new Map<SymbolId, number>();
    for (const s of paying) minCount.set(s.id, Math.min(...Object.keys(s.pays!).map(Number)));
    t = { defs, paying, minCount };
    cache.set(config, t);
  }
  return t;
}

function round2(x: number): number {
  return Math.round(x * 100) / 100;
}

/** Evaluate a landed grid. Money amounts are in currency units (bet = total bet). */
export function evaluate(config: MathConfig, grid: Grid, bet: number, mode: GameMode): EvalResult {
  const { defs, paying, minCount } = tables(config);
  const modeMult = mode === 'free' ? config.freeSpins.multiplier : 1;
  const lineBet = bet / config.paylines.length;
  const lineWins: LineWin[] = [];

  config.paylines.forEach((rows, lineIndex) => {
    // Symbols along the line, left to right.
    const line: SymbolDef[] = [];
    for (let reel = 0; reel < config.reels; reel++) {
      const def = defs.get(grid[reel][rows[reel]]);
      if (!def) throw new Error(`Unknown symbol ${grid[reel][rows[reel]]} at reel ${reel}`);
      line.push(def);
    }
    let best: { symbol: SymbolId; count: number; pay: number; wildMult: number } | null = null;
    for (const sym of paying) {
      let count = 0;
      let wildMult = 1;
      for (const def of line) {
        if (def.id === sym.id) count++;
        else if (def.wild) {
          count++;
          wildMult = Math.max(wildMult, def.multiplier ?? 1);
        } else break;
      }
      if (count < (minCount.get(sym.id) ?? 3)) continue;
      const pay = (sym.pays![count] ?? 0) * wildMult;
      if (pay > 0 && (!best || pay > best.pay)) best = { symbol: sym.id, count, pay, wildMult };
    }
    if (best) {
      const positions: Position[] = [];
      for (let reel = 0; reel < best.count; reel++) positions.push([reel, rows[reel]]);
      lineWins.push({
        lineIndex,
        symbol: best.symbol,
        count: best.count,
        positions,
        payout: round2(best.pay * lineBet * modeMult),
      });
    }
  });

  // Scatter: anywhere on the grid (games without a scatter skip this).
  const sc = config.scatter;
  let scatterWin: ScatterWin | null = null;
  let freeSpinsAwarded = 0;
  if (sc) {
    const scatterPositions: Position[] = [];
    for (let reel = 0; reel < config.reels; reel++) {
      for (let row = 0; row < config.rows; row++) if (grid[reel][row] === sc.symbol) scatterPositions.push([reel, row]);
    }
    const count = scatterPositions.length;
    if (count >= sc.minCount) {
      const mult = sc.pays[count] ?? 0;
      scatterWin = { count, positions: scatterPositions, payout: round2(mult * bet * modeMult) };
      if (mode === 'base' || sc.retrigger) freeSpinsAwarded = sc.freeSpins[count] ?? 0;
    }
  }

  let total = lineWins.reduce((s, w) => s + w.payout, 0) + (scatterWin?.payout ?? 0);
  total = round2(total);
  const cap = round2(bet * config.maxWinMultiplier);
  const capped = total > cap;
  if (capped) total = cap;
  return { lineWins, scatterWin, freeSpinsAwarded, totalWin: total, capped };
}
